import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faqAPI } from '../services/api';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
  }, []);

  // Handle URL hash for direct linking to a question
  useEffect(() => {
    if (location.hash && faqs.length > 0) {
      const id = parseInt(location.hash.replace('#faq-', ''));
      if (id) {
        setExpandedIds(new Set([id]));
        // Scroll to the element after a short delay
        setTimeout(() => {
          const element = document.getElementById(`faq-${id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [location.hash, faqs]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [faqData, categoryData] = await Promise.all([
        faqAPI.getAll(),
        faqAPI.getCategories(),
      ]);
      setFaqs(faqData);
      setCategories(categoryData);
    } catch (err) {
      console.error('Error loading FAQs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter FAQs based on search and category
  const filteredFaqs = useMemo(() => {
    let result = faqs;

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter((faq) => faq.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query) ||
          (faq.keywords && faq.keywords.toLowerCase().includes(query))
      );
    }

    return result;
  }, [faqs, selectedCategory, searchQuery]);

  // Group filtered FAQs by category
  const groupedFaqs = useMemo(() => {
    return filteredFaqs.reduce((acc, faq) => {
      if (!acc[faq.category]) {
        acc[faq.category] = [];
      }
      acc[faq.category].push(faq);
      return acc;
    }, {});
  }, [filteredFaqs]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(filteredFaqs.map((faq) => faq.id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Highlight matching text in search results
  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: '#f0a500', padding: '0 2px' }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleLinkClick = (e, link) => {
    e.stopPropagation();
    navigate(link);
  };

  if (isLoading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-2">Frequently Asked Questions</h2>
        <p className="text-muted">
          Find answers to common questions about using the Drummond Portal.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="row mb-4">
        <div className="col-md-6 mb-3 mb-md-0">
          <div className="input-group">
            <span className="input-group-text">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <div className="btn-group w-100">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={expandAll}
              title="Expand all"
            >
              Expand
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={collapseAll}
              title="Collapse all"
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <div className="mb-3 text-muted">
          Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
        </div>
      )}

      {/* FAQ Content */}
      {filteredFaqs.length === 0 ? (
        <div className="text-center py-5">
          <div className="text-muted">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              fill="currentColor"
              viewBox="0 0 16 16"
              className="mb-3"
            >
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z" />
            </svg>
            <h5>No FAQs found</h5>
            <p>Try adjusting your search or category filter.</p>
          </div>
        </div>
      ) : (
        <div className="accordion" id="faqAccordion">
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="mb-4">
              <h5 className="text-primary border-bottom pb-2 mb-3">
                {category}
                <span className="badge bg-secondary ms-2 fs-6">
                  {categoryFaqs.length}
                </span>
              </h5>

              {categoryFaqs
                .sort((a, b) => a.display_order - b.display_order)
                .map((faq) => (
                  <div
                    key={faq.id}
                    id={`faq-${faq.id}`}
                    className="card mb-2"
                    style={{
                      borderColor: expandedIds.has(faq.id) ? '#072040' : '#dee2e6',
                    }}
                  >
                    <div
                      className="card-header d-flex justify-content-between align-items-center"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: expandedIds.has(faq.id) ? '#f8f9fa' : 'white',
                      }}
                      onClick={() => toggleExpand(faq.id)}
                    >
                      <div className="d-flex align-items-center flex-grow-1">
                        <span
                          className="me-3"
                          style={{
                            transition: 'transform 0.2s',
                            transform: expandedIds.has(faq.id)
                              ? 'rotate(90deg)'
                              : 'rotate(0deg)',
                          }}
                        >
                          ▶
                        </span>
                        <strong>
                          {searchQuery
                            ? highlightText(faq.question, searchQuery)
                            : faq.question}
                        </strong>
                      </div>
                      {faq.related_link && (
                        <button
                          className="btn btn-sm btn-outline-primary ms-2"
                          onClick={(e) => handleLinkClick(e, faq.related_link)}
                          title="Go to related section"
                        >
                          Open →
                        </button>
                      )}
                    </div>
                    {expandedIds.has(faq.id) && (
                      <div className="card-body">
                        <div style={{ whiteSpace: 'pre-line' }}>
                          {searchQuery
                            ? highlightText(faq.answer, searchQuery)
                            : faq.answer}
                        </div>
                        {faq.related_link && (
                          <div className="mt-3">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={(e) => handleLinkClick(e, faq.related_link)}
                            >
                              Go to {faq.related_link.replace('/apps/', '').replace('-', ' ')} →
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}

      {/* Back to Dashboard */}
      <div className="mt-4 pt-4 border-top">
        <button className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default FAQPage;
